import React, { useId, useRef, useState } from "react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { signInWithGoogle, signInWithApple, formatAuthError } from "../lib/supabaseAuth";
import SocialAuthButtons from "./SocialAuthButtons";
import FormLabel from "./FormLabel";
import {
  WorkflowModalBody,
  WorkflowModalHeader,
  WorkflowModalShell,
} from "./workflow/WorkflowModal";

/**
 * Inline sign-in for Google / Apple — same options as LoginPage.
 * Phone OTP is not offered here (not enabled for this product surface).
 */
export default function AuthModal({ open, onClose, onSuccess, title, subtitle }) {
  const { signIn } = useAuth();
  const dialogRef = useRef(null);
  const titleId = useId();
  const subtitleId = useId();
  const [loadingProvider, setLoadingProvider] = useState(null);

  if (!open) return null;

  const loading = loadingProvider !== null;

  function close() {
    setLoadingProvider(null);
    onClose?.();
  }

  function handleSuccess(data) {
    signIn({ token: data.token, account: data.organizer, dancer_id: data.dancer_id });
    onSuccess?.({
      account: data.organizer,
      newAccount: data.new_account,
      dancer_id: data.dancer_id,
    });
    setLoadingProvider(null);
  }

  async function handleSocialSignIn(provider, fn) {
    setLoadingProvider(provider);
    try {
      const data = await fn();
      // OAuth providers redirect; phone-style inline session would return data.
      if (data) handleSuccess(data);
    } catch (err) {
      const msg = err?.response ? formatApiError(err) : formatAuthError(err);
      if (msg !== "Sign-in cancelled.") toast.error(msg);
      setLoadingProvider(null);
    }
  }

  const returnTo =
    typeof window !== "undefined"
      ? `${window.location.pathname}${window.location.search}`
      : "/";

  return (
    <WorkflowModalShell
      dialogRef={dialogRef}
      onClose={close}
      testId="auth-modal"
      titleId={titleId}
      describedById={subtitle ? subtitleId : undefined}
      busy={loading}
      maxWidthClass="sm:max-w-[480px]"
    >
      <WorkflowModalHeader
        title={title || "Sign in to continue"}
        titleId={titleId}
        subtitle={
          subtitle ? (
            <p id={subtitleId} className="font-ui font-medium text-[13px] text-ink/80 leading-relaxed text-pretty">
              {subtitle}
            </p>
          ) : null
        }
        onClose={close}
        closeTestId="auth-modal-close"
        sticky
      />
      <WorkflowModalBody>
        <fieldset className="flex flex-col gap-4 border-0 p-0 m-0 min-w-0" disabled={loading}>
          <legend className="sr-only">Sign-in options</legend>
          <div aria-live="polite" aria-busy={loading}>
            <FormLabel as="p" id="auth-modal-sign-in-label" className="mb-3 block">
              Choose Google or Apple
            </FormLabel>
            <SocialAuthButtons
              readable
              stacked
              loading={loading}
              loadingProvider={loadingProvider}
              aria-labelledby="auth-modal-sign-in-label"
              onGoogle={() =>
                handleSocialSignIn("google", () => signInWithGoogle(returnTo))
              }
              onApple={() =>
                handleSocialSignIn("apple", () => signInWithApple(returnTo))
              }
            />
          </div>
        </fieldset>
      </WorkflowModalBody>
    </WorkflowModalShell>
  );
}
